<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <div class="flex flex-col gap-4">
        <!-- Timeline navigation, one tab per moment -->
        <BaseTabs
            v-if="moments.length > 0"
            v-model="activeTabId"
            :tab-ids="tabIds"
            :labels="tabLabels"
            :disabled-tab-ids="lockedTabIds"
            list-class="max-w-3xl mx-auto"
            content-class="pt-4">
            <template #tab="{ tabId, selected }">
                <MomentsTimelineGen1 :date="formatDate(momentsByTabId[tabId].start_date)" :state="momentsByTabId[tabId].state" :is-active="selected" />
            </template>
            <template v-for="moment in moments" :key="moment.id" #[`content-${moment.id}`]>
                <PerformanceMoment :moment="moment" />
            </template>
        </BaseTabs>

        <NoMomentsNotification v-else />

        <template v-if="organizationCards.length > 0">
            <!-- Same column and section spacing as the moment content above. -->
            <div class="max-w-4xl mx-auto w-full px-4 mt-6">
                <h3 class="scaffold-body mb-4">{{ t("moments.page.organizationTopicsHeading", { organizationName }) }}</h3>
                <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <NewHomeCard
                        v-for="card in organizationCards"
                        :key="card.id"
                        :card-type="card.type"
                        :title="card.title"
                        :subtitle="card.subtitle"
                        :content="card.content"
                        :cta-button-text="card.ctaButtonText"
                        @cta-click="handleOrganizationCardClick(card)">
                        <template #header>
                            <img v-if="card.image_url" :src="card.image_url" :alt="card.title || t('moments.page.cardImageAlt')" class="h-10 w-auto object-contain max-w-[200px]" />
                            <img
                                v-else-if="organizationDetails.logo_url"
                                :src="organizationDetails.logo_url"
                                :alt="organizationDetails.name || t('moments.page.organizationLogoAlt')"
                                class="h-10 w-auto object-contain max-w-[200px]" />
                            <i v-else class="bi bi-people text-3xl/none" aria-hidden="true"></i>
                        </template>
                    </NewHomeCard>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import NewHomeCard from "~vue/ChatHome/NewHomeCard.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import MomentsTimelineGen1 from "~vue/components/moments/MomentsTimelineGen1.vue";
import NoMomentsNotification from "~vue/Moments/NoMomentsNotification.vue";
import PerformanceMoment from "~vue/Moments/PerformanceMoment.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants";
import { formatDate as localizedDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

const { moments } = defineProps({
    moments: {
        type: Array,
        default: () => [],
    },
    organizationCards: {
        type: Array,
        default: () => [],
    },
});

const { t } = useI18n();

const getInitialMomentSelection = () => {
    // Deep link from the homepage banner's per-moment buttons (ENG-1330)
    const momentIdParam = new URLSearchParams(globalThis.location.search).get("moment_id");
    if (momentIdParam) {
        const requestedMoment = moments.find((m) => m.id === Number(momentIdParam) && m.state !== "locked");
        if (requestedMoment) {
            return requestedMoment;
        }
    }
    const latestMoments = [...moments].reverse();
    return latestMoments.find((m) => m.state === "open") || latestMoments.find((m) => m.state === "completed") || latestMoments.find((m) => m.state === "expired") || moments[0];
};
const defaultActiveMoment = getInitialMomentSelection();
const activeMomentId = ref(defaultActiveMoment?.id || null);

// BaseTabs keys tabs by string; moments are keyed by number.
const tabIds = computed(() => moments.map((moment) => String(moment.id)));
const momentsByTabId = computed(() => Object.fromEntries(moments.map((moment) => [String(moment.id), moment])));
const tabLabels = computed(() => Object.fromEntries(moments.map((moment) => [String(moment.id), moment.title])));
// A locked moment has not opened yet, so it cannot be selected.
const lockedTabIds = computed(() => moments.filter((moment) => moment.state === "locked").map((moment) => String(moment.id)));
const activeTabId = computed({
    get: () => (activeMomentId.value === null ? "" : String(activeMomentId.value)),
    set: (tabId) => {
        activeMomentId.value = Number(tabId);
    },
});

const formatDate = (date) => {
    if (!date) {
        return "";
    }
    return localizedDate(date, "month-year");
};

const organizationDetails = inject("organization_details");
const organizationName = computed(() => {
    {
        if (organizationDetails.value?.is_default || !organizationDetails.value?.name) {
            return t("moments.page.defaultOrganizationName");
        }
        return organizationDetails.value.name;
    }
});
const handleOrganizationCardClick = (card) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, { card_type: card.type, location: HOMEPAGE_CARD_LOCATION.MOMENTS });
    openUrl(card.url, { queryParams: { chat_source: CHATSOURCE.TRENDING_ORG_PROMPT } });
};
</script>
